import type { SignalStore, StoreState } from '@awork/core/state/signal-store/signalStore'
import type { Signal } from '@angular/core'
import { computed } from '@angular/core'
import { getState } from '@ngrx/signals'

/**
 * Query class for signal stores.
 * Provides functions to query the store.
 *
 * ### Usage example
 * ```ts
 * @Injectable({ providedIn: 'root' })
 * export class AppQuery extends SignalQuery<AppState> {
 *   constructor(private appStore: AppStore) {
 *     super(appStore)
 *   }
 * }
 * ```
 */
export class SignalQuery<State extends object = object> {
  private readonly signalStore: SignalStore<State>
  private readonly state: StoreState<State>
  private readonly stateSignal: Signal<State>

  constructor(store: SignalStore<State>) {
    this.signalStore = store
    this.state = store.getState()
    this.stateSignal = computed(() => getState(this.state))
  }

  /**
   * Gets the loading state
   * @returns {Signal<boolean>}
   */
  get queryLoading(): Signal<boolean> {
    return this.signalStore.getIsLoading()
  }

  /**
   * Gets the loaded on date
   * @returns {Signal<Date | null>}
   */
  get queryLoadedOn(): Signal<Date | null> {
    return computed(() => {
      const loadedOn = this.signalStore.getLoadedOn()
      return loadedOn() ? new Date(loadedOn()) : null
    })
  }

  /**
   * Gets the error state
   * @returns {Signal<Error>}
   */
  queryError(): Signal<Error> {
    return this.signalStore.getError()
  }

  /**
   * Gets the value of the state
   * @returns {State}
   */
  getValue(): State {
    return getState(this.state)
  }

  /**
   * Gets the state
   * Optionally, it can get a property of the state
   * @returns {Signal<State>}
   */
  query(): Signal<State>
  query<K extends keyof State>(property: K): Signal<State[K]>
  query<K extends keyof State>(property?: K): Signal<State | State[K]> {
    if (property) {
      return this.state[property] as Signal<State[K]>
    }

    return this.stateSignal
  }
}
